
import {
  collection,
  query,
  getDocs,
  limit,
  startAfter,
  orderBy,
  where,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

export interface VariantSelection {
  variantType: string;
  variantValue: string;
}

export interface WarehouseAllocation {
  warehouseId: string;
  warehouseName: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
}

export interface ProductVariant {
  id: string;
  sku: string;
  price: number;
  discount?: number; // Discount percentage (0-100)
  stockQuantity: number;
  isDefault: boolean;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  minStockLevel: number;
  maxStockLevel: number;
  variantSelections: VariantSelection[];
  warehouseAllocations: WarehouseAllocation[];
  requiresSerialTracking?: boolean;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discount?: number; // Discount percentage (0-100)
  sku: string;
  categoryId: string;
  subcategoryId: string;
  brandId?: string | null;
  images: string[];
  hasVariants: boolean;
  variants: ProductVariant[];
  stockQuantity: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isFeatured?: boolean;
  minStockLevel: number;
  maxStockLevel: number;
  tags?: string[];
  requiresSerialTracking?: boolean;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PaginatedProducts {
  products: Product[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export type SortOption = 'newness' | 'price-low' | 'price-high';

const PRODUCTS_PER_PAGE = 12;
const WAREHOUSE_INVENTORY_COLLECTION = 'warehouse_inventory';
const WAREHOUSES_COLLECTION = 'warehouses';

export interface ProductFilters {
  categoryId?: string;
  subcategoryId?: string;  // ← NEW: filter by product.subcategoryId field
  brandId?: string;
  searchQuery?: string;
  tags?: string[];
  sortBy?: SortOption;
}

// ── Utility: calculate discounted price ──────────────────────────────────────
export const calculateDiscountedPrice = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) return price;
  return price - (price * discount) / 100;
};

export const hasDiscount = (discount?: number): boolean => {
  return !!discount && discount > 0 && discount <= 100;
};

// ── Fetch total stock for a product across ALL warehouses ─────────────────────
export const fetchProductTotalStock = async (
  productId: string,
  variantId?: string
): Promise<number> => {
  try {
    const warehousesRef = collection(db, WAREHOUSES_COLLECTION);
    const warehousesSnap = await getDocs(
      query(warehousesRef, where('isActive', '==', true))
    );

    let totalStock = 0;
    const docId = variantId ? `${productId}_${variantId}` : productId;

    await Promise.all(
      warehousesSnap.docs.map(async (warehouseDoc) => {
        const inventoryRef = doc(
          db,
          WAREHOUSE_INVENTORY_COLLECTION,
          warehouseDoc.id,
          'products',
          docId
        );
        const inventorySnap = await getDoc(inventoryRef);
        if (inventorySnap.exists()) {
          totalStock += inventorySnap.data().quantity || 0;
        }
      })
    );

    return totalStock;
  } catch (error) {
    console.error('Error fetching total stock across warehouses:', error);
    return 0;
  }
};

// ── Fetch stock for ALL variants of a product across all warehouses ───────────
export const fetchProductAllVariantsStock = async (
  productId: string,
  variantIds: string[]
): Promise<number> => {
  try {
    const warehousesRef = collection(db, WAREHOUSES_COLLECTION);
    const warehousesSnap = await getDocs(
      query(warehousesRef, where('isActive', '==', true))
    );

    let totalStock = 0;

    await Promise.all(
      warehousesSnap.docs.map(async (warehouseDoc) => {
        await Promise.all(
          variantIds.map(async (variantId) => {
            const docId = `${productId}_${variantId}`;
            const inventoryRef = doc(
              db,
              WAREHOUSE_INVENTORY_COLLECTION,
              warehouseDoc.id,
              'products',
              docId
            );
            const inventorySnap = await getDoc(inventoryRef);
            if (inventorySnap.exists()) {
              totalStock += inventorySnap.data().quantity || 0;
            }
          })
        );
      })
    );

    return totalStock;
  } catch (error) {
    console.error('Error fetching all variant stock across warehouses:', error);
    return 0;
  }
};

// ── Enrich a product's isOutOfStock by querying warehouse_inventory ───────────
export const enrichProductStockFromWarehouses = async (
  product: Product
): Promise<Product> => {
  try {
    let totalStock = 0;

    if (!product.hasVariants) {
      totalStock = await fetchProductTotalStock(product.id);
    } else if (product.variants && product.variants.length > 0) {
      const variantIds = product.variants
        .map((v) => v.id)
        .filter(Boolean) as string[];
      totalStock = await fetchProductAllVariantsStock(product.id, variantIds);
    }

    return {
      ...product,
      stockQuantity: totalStock,
      isOutOfStock: totalStock === 0,
    };
  } catch (error) {
    console.error('Error enriching product stock:', error);
    return product;
  }
};

export const fetchProducts = async (
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (lastDoc) {
      q = query(
        productsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
    } else {
      q = query(
        productsRef,
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }

    const snapshot = await getDocs(q);
    const products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
    const hasMore = snapshot.docs.length === pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const fetchProductsByCategory = async (
  categoryId: string,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (categoryId === '*') {
      if (lastDoc) {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      } else {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        );
      }
    } else {
      if (lastDoc) {
        q = query(
          productsRef,
          where('categoryId', '==', categoryId),
          startAfter(lastDoc),
          limit(pageSize + 10)
        );
      } else {
        q = query(
          productsRef,
          where('categoryId', '==', categoryId),
          limit(pageSize + 10)
        );
      }
    }

    const snapshot = await getDocs(q);
    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    if (categoryId !== '*') {
      products.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
      products = products.slice(0, pageSize);
    }

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = snapshot.docs.length > pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

export const fetchFilteredProducts = async (
  filters: ProductFilters,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    console.log('Fetching products with filters:', filters);
    const productsRef = collection(db, 'products');
    const fetchSize = pageSize * 3;
    let q;
    const constraints: any[] = [];

    // ── Subcategory filter — takes priority over categoryId ───────────────────
    // products store their sub's ID in product.subcategoryId
    // (Firestore doc field: subcategoryId = "XYZ", categoryId = parent's ID)
    if (filters.subcategoryId && filters.subcategoryId !== '*' && filters.subcategoryId !== 'undefined') {
      console.log('Adding subcategory filter:', filters.subcategoryId);
      constraints.push(where('subcategoryId', '==', filters.subcategoryId));
    }
    // ── Parent category filter (only when subcategoryId is NOT set) ───────────
    else if (filters.categoryId && filters.categoryId !== '*' && filters.categoryId !== 'undefined') {
      console.log('Adding category filter:', filters.categoryId);
      constraints.push(where('categoryId', '==', filters.categoryId));
    }

    // ── Brand filter ──────────────────────────────────────────────────────────
    if (filters.brandId && filters.brandId !== '*' && filters.brandId !== 'undefined') {
      console.log('Adding brand filter:', filters.brandId);
      constraints.push(where('brandId', '==', filters.brandId));
    }

    // ── Tag filter (single tag only — Firestore array-contains limit) ─────────
    if (filters.tags && filters.tags.length === 1) {
      console.log('Adding tag filter:', filters.tags[0]);
      constraints.push(where('tags', 'array-contains', filters.tags[0]));
    }

    if (constraints.length > 0) {
      if (lastDoc) {
        q = query(
          productsRef,
          ...constraints,
          startAfter(lastDoc),
          limit(fetchSize)
        );
      } else {
        q = query(
          productsRef,
          ...constraints,
          limit(fetchSize)
        );
      }
    } else {
      if (lastDoc) {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(fetchSize)
        );
      } else {
        q = query(
          productsRef,
          orderBy('createdAt', 'desc'),
          limit(fetchSize)
        );
      }
    }

    const snapshot = await getDocs(q);
    console.log('Fetched documents count:', snapshot.docs.length);

    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    console.log('Products before filtering:', products.length);

    // ── Search filter (client-side) ───────────────────────────────────────────
    if (filters.searchQuery?.trim()) {
      const searchLower = filters.searchQuery.toLowerCase();
      products = products.filter(product =>
        product.name.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower) ||
        (Array.isArray(product.tags) && product.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
      console.log('Products after search filter:', products.length);
    }

    // ── Multi-tag filter (client-side, >1 tag) ────────────────────────────────
    if (filters.tags && filters.tags.length > 1) {
      products = products.filter(product =>
        product.tags &&
        Array.isArray(product.tags) &&
        filters.tags!.some(tag => product.tags!.includes(tag))
      );
      console.log('Products after tags filter:', products.length);
    }

    // ── Sort ──────────────────────────────────────────────────────────────────
    switch (filters.sortBy) {
      case 'newness':
        products.sort((a, b) => {
          const aTime = a.createdAt?.toMillis() || 0;
          const bTime = b.createdAt?.toMillis() || 0;
          return bTime - aTime;
        });
        break;
      case 'price-low':
        products.sort((a, b) => {
          const aInfo = getProductDisplayInfo(a);
          const bInfo = getProductDisplayInfo(b);
          return aInfo.discountedPrice - bInfo.discountedPrice;
        });
        break;
      case 'price-high':
        products.sort((a, b) => {
          const aInfo = getProductDisplayInfo(a);
          const bInfo = getProductDisplayInfo(b);
          return bInfo.discountedPrice - aInfo.discountedPrice;
        });
        break;
    }

    const paginatedProducts = products.slice(0, pageSize);

    // ── Enrich stock from warehouses in parallel ──────────────────────────────
    const enrichedProducts = await Promise.all(
      paginatedProducts.map((product) => enrichProductStockFromWarehouses(product))
    );

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = products.length > pageSize;

    console.log('Returning products:', enrichedProducts.length, 'hasMore:', hasMore);

    return {
      products: enrichedProducts,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching filtered products:', error);
    throw error;
  }
};

export const fetchAvailableTags = async (): Promise<string[]> => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(productsRef, limit(1000));
    const snapshot = await getDocs(q);

    const tagsSet = new Set<string>();
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.tags && Array.isArray(data.tags)) {
        data.tags.forEach((tag: string) => tagsSet.add(tag));
      }
    });

    return Array.from(tagsSet).sort();
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
};

export const fetchProductsBySubcategory = async (
  subcategoryId: string,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null,
  pageSize: number = PRODUCTS_PER_PAGE
): Promise<PaginatedProducts> => {
  try {
    const productsRef = collection(db, 'products');
    let q;

    if (lastDoc) {
      q = query(
        productsRef,
        where('subcategoryId', '==', subcategoryId),
        startAfter(lastDoc),
        limit(pageSize + 10)
      );
    } else {
      q = query(
        productsRef,
        where('subcategoryId', '==', subcategoryId),
        limit(pageSize + 10)
      );
    }

    const snapshot = await getDocs(q);
    let products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    products.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    products = products.slice(0, pageSize);

    const lastVisible = snapshot.docs[Math.min(snapshot.docs.length - 1, pageSize - 1)] || null;
    const hasMore = snapshot.docs.length > pageSize;

    return {
      products,
      lastDoc: lastVisible,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching products by subcategory:', error);
    throw error;
  }
};

// ── Core display info — now includes discount fields ─────────────────────────
export const getProductDisplayInfo = (product: Product) => {
  if (!product.hasVariants || !product.variants || product.variants.length === 0) {
    const price = product.price;
    const discount = product.discount;
    const discountedPrice = calculateDiscountedPrice(price, discount);
    return {
      price,
      discount,
      discountedPrice,
      hasDiscount: hasDiscount(discount),
      stockQuantity: product.stockQuantity,
      isOutOfStock: product.isOutOfStock,
      sku: product.sku
    };
  }

  const variant = product.variants.find(v => v.isDefault) || product.variants[0];
  const price = variant.price;
  const discount = variant.discount;
  const discountedPrice = calculateDiscountedPrice(price, discount);

  return {
    price,
    discount,
    discountedPrice,
    hasDiscount: hasDiscount(discount),
    stockQuantity: variant.stockQuantity,
    isOutOfStock: product.isOutOfStock,
    sku: variant.sku,
    variant
  };
};

export const getVariantDisplayText = (variant: ProductVariant): string => {
  if (!variant.variantSelections || variant.variantSelections.length === 0) {
    return '';
  }
  return variant.variantSelections
    .map(selection => selection.variantValue)
    .join(' / ');
};

export const fetchFeaturedProducts = async (maxProducts: number = 10): Promise<Product[]> => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('isFeatured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(maxProducts)
    );

    const snapshot = await getDocs(q);
    const products: Product[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Product));

    const enriched = await Promise.all(
      products.map((p) => enrichProductStockFromWarehouses(p))
    );

    return enriched;
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

export const fetchProductById = async (productId: string): Promise<Product | null> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists()) {
      const product = {
        id: productSnap.id,
        ...productSnap.data()
      } as Product;

      return await enrichProductStockFromWarehouses(product);
    } else {
      console.log('No such product!');
      return null;
    }
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};